import type { ThunkDispatch } from 'editor/src/store/hooks';
import isPageCountOptionsEnabled from 'editor/src/store/hostSettings/selector/isPageCountOptionsEnabled';
import { ECOMMERCE_PAGE_COUNT_CONTROL_NAME } from 'editor/src/store/variants/constants';
import getECommercePageCountOptionIndex from 'editor/src/store/variants/helpers/getECommercePageCountOptionIndex';
import getECommerceProduct from 'editor/src/store/variants/selector/getECommerceProduct';
import { setECommerceProductIdAction } from 'editor/src/store/variants/slice';

import { captureException } from 'editor/src/logger';
import { RootState } from 'editor/src/store';
import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import { getSelectedVariant } from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/ProductControlContent/ECommerceProductContent/eCommerceVariantUtils';

const changeEcommerceVariantPageCountOperation =
  (pageCount: number) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    const ecommerceProduct = getECommerceProduct(state);
    if (!ecommerceProduct?.variants || !pageCount) {
      return;
    }

    const pageCountOptionsEnabled = isPageCountOptionsEnabled(state);
    const controlIndex = pageCountOptionsEnabled
      ? getECommercePageCountOptionIndex(ecommerceProduct)
      : ecommerceProduct.options.findIndex((option) => option.name === ECOMMERCE_PAGE_COUNT_CONTROL_NAME);
    const selectedVariant = ecommerceProduct.variants.find(
      (variant) => variant.id === ecommerceProduct.selectedVariantId,
    );
    if (!selectedVariant || controlIndex === -1) {
      return;
    }

    // Only the variant that keeps every other option: falling back to one that sells this page count
    // in another size would switch the size under the buyer.
    // Until the host sends `enablePageCountOptions`, the closest variant selling the page count is taken.
    const newVariant = pageCountOptionsEnabled
      ? ecommerceProduct.variants.find((variant) =>
          variant.optionValues.every((value, index) =>
            index === controlIndex ? parseInt(value, 10) === pageCount : value === selectedVariant.optionValues[index],
          ),
        )
      : getSelectedVariant(ecommerceProduct.variants, selectedVariant, controlIndex, pageCount.toString());

    if (newVariant) {
      dispatch(setECommerceProductIdAction(newVariant.id));
      sendPostMessage('variants.requestECommerceVariant', { gelatoVariantId: newVariant.id });
    } else {
      const error = new Error(`Variant is missed for page count ${pageCount}`);
      const extra = { ecommerceProduct };
      // eslint-disable-next-line no-console
      console.error(error, extra);
      captureException(error, { extra });
    }
  };

export default changeEcommerceVariantPageCountOperation;
